import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import type { ProjectOnList } from '@/api/v4/projects.api';
import { fetchProjectListings } from '@/api/v4/projects.api';
import { QueryKey } from '@/config/query-client';
import { selectedCompaniesState } from '@/features/companies/companies.state';
import {
  addCompanyInProgressState,
  companyIdState,
} from '@/features/companies/overview/company.state';
import { useCompanyFetchMode } from '@/features/companies/overview/company-fetch-context';
import { useCreateCompany } from '@/hooks/queries/use-create-company.mutation';
import { paths } from '@/routes/helpers/paths';
import { notify } from '@/ui/snackbar/notify';
import { useTranslateHeaders } from '@/ui/table/helpers/use-translate-headers';
import { EllipsisTextTooltip } from '@/ui/table/infinite-table/ellipsis-text-tooltip';
import {
  TablePrimaryCell,
  TableSecondaryCell,
} from '@/ui/table/infinite-table/table-cells';
import type { Row } from '@/ui/table/table.types';

import { useCreateProjectListings } from '../project-listings/use-create-project-listings.mutation';
import { useProjectListingsSectionParams } from '../project-listings/use-project-listings-section-params';

import { projectsHeaders } from './projects-table-configuration';
import { ProjectStageCell } from './components/project-stage-cell';
import { ProjectCreatedForCell } from './components/project-created-for-cell';
import { useSetAtom, useAtomValue } from 'jotai';

export const useProjectsForCompanyTableConfiguration = (
  onSelect: (projectName: ProjectOnList['name'], successCount: number) => void,
  onLinkClick: () => void,
) => {
  const { t } = useTranslation('projects');
  const queryClient = useQueryClient();

  const { createCompanyFromCrunchbase } = useCreateCompany();
  const setIsAddingCompanyToProjectInProgress = useSetAtom(
    addCompanyInProgressState,
  );
  const header = useTranslateHeaders(projectsHeaders, 'projects', 'projects');
  const selectedCompanies = useAtomValue(selectedCompaniesState);
  const companyId = useAtomValue(companyIdState);
  const fetchMode = useCompanyFetchMode();
  const { createProjectListing } = useCreateProjectListings();
  const listingsSectionParams = useProjectListingsSectionParams();

  const getAlreadyAdded = useCallback(
    async (projectId: string) => {
      const projectListings = await fetchProjectListings(projectId);
      return selectedCompanies.filter(selected =>
        projectListings?.records?.some(
          item => item.organization.id === selected.organizationId,
        ),
      ).length;
    },
    [selectedCompanies],
  );

  const addCompanyToProjects = useCallback(
    async (project: ProjectOnList) => {
      try {
        setIsAddingCompanyToProjectInProgress(true);

        if (!selectedCompanies.length) return;

        const alreadyAddedCount = await getAlreadyAdded(project.id);

        const existingSavvyCompanies = selectedCompanies
          .filter(company => company.organizationId)
          .map(company => company.organizationId as string);

        // create new savvy companies first
        const newCrunchbaseCompanies = selectedCompanies
          .filter(company => company.crunchbaseId && !company.organizationId)
          .map(company => company.crunchbaseId as string);
        if (newCrunchbaseCompanies.length) {
          const result = await Promise.all(
            newCrunchbaseCompanies.map(item =>
              createCompanyFromCrunchbase({ cbId: item, source: 'SOSA Q' }),
            ),
          );

          const newSavvyIds = result.map(item => item.id);
          existingSavvyCompanies.push(...newSavvyIds);
        }

        const result = await createProjectListing(project.id, {
          companyIds: existingSavvyCompanies,
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKey.Company, fetchMode, companyId],
        });
        const failed = result.filter(item => item.error);
        if (failed.length)
          console.warn('Some companies were not added', failed);
        onSelect(project.name, result.length - failed.length);

        if (alreadyAddedCount > 0) {
          notify({
            message: t('companies:companies.alreadyAddedToProject', {
              count: alreadyAddedCount,
            }),
          });
        }
      } finally {
        setIsAddingCompanyToProjectInProgress(false);
      }
    },
    [
      setIsAddingCompanyToProjectInProgress,
      selectedCompanies,
      getAlreadyAdded,
      createProjectListing,
      queryClient,
      companyId,
      onSelect,
      createCompanyFromCrunchbase,
      t,
    ],
  );

  const handleAddProject = useCallback(
    async (project: ProjectOnList) => {
      await addCompanyToProjects(project);
    },
    [addCompanyToProjects],
  );

  const mapProjectToRow = useCallback(
    (project: ProjectOnList): Row => {
      return {
        id: project.id,
        rowHover: true,
        onClick: async event => {
          const target = event.target as HTMLTableRowElement;
          if (target.tagName.toLowerCase() === 'a') return;
          await handleAddProject(project);
        },
        cells: [
          {
            width: '450px',
            value: (
              <>
                <TablePrimaryCell maxWidth={460}>
                  <EllipsisTextTooltip
                    onClick={onLinkClick}
                    text={project.name}
                    Component={
                      <Link
                        to={{
                          pathname: paths.project({ projectId: project.id }),
                          search: listingsSectionParams,
                        }}
                        onClick={onLinkClick}
                      />
                    }
                  />
                </TablePrimaryCell>
                {project.listingCount > 0 ? (
                  <TableSecondaryCell>
                    <Link
                      to={{
                        pathname: paths.project({ projectId: project.id }),
                        search: listingsSectionParams,
                      }}
                      onClick={onLinkClick}
                    >
                      {t('projects.company', {
                        count: project.listingCount,
                      })}
                    </Link>
                  </TableSecondaryCell>
                ) : (
                  <TableSecondaryCell>-</TableSecondaryCell>
                )}
              </>
            ),
          },
          {
            padding: '0',
            value: (
              <ProjectStageCell stage={project.mostAdvancedListingStatus} />
            ),
          },
          {
            value: (
              <ProjectCreatedForCell createdFor={project.createdForTeam} />
            ),
          },
        ],
      };
    },
    [handleAddProject, listingsSectionParams, onLinkClick, t],
  );

  return {
    mapProjectToRow,
    header,
  };
};
